import EmptyState from '@local/web-design-system-2/dist/components/EmptyState/EmptyState';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';

import { useGetSubscriptionByIdQuery } from 'src/apiClients';
import { i18n as routesI18n } from 'src/hooks/use-imago-mp-routes.i18n';
import { useScrollSpy } from 'src/hooks/useScrollSpy';

import { type EntityStatusTone, TONE_TO_CHIP_COLOR } from './entity-status-tone';
import { InactiveInstanceBanner } from './inactive-instance-banner/InactiveInstanceBanner';
import { InstanceContext } from './InstanceContext';
import { i18n } from './InstanceDetailPage.i18n';
import { useStyles } from './InstanceDetailPage.styles';
import { INSTANCE_SECTIONS } from './InstanceDetailSections';
import { SubSideBar } from './sub-side-bar/SubSideBar';

export { useInstance } from './InstanceContext';

const STATE_TO_TONE: Record<string, EntityStatusTone> = {
    A: 'active',
    I: 'inactive',
    X: 'archived',
};

const SECTION_IDS = INSTANCE_SECTIONS.map((s) => s.id);

export const InstanceDetailPage = () => {
    const intl = useIntl();
    const { classes } = useStyles();
    const { id } = useParams<{ id: string }>();
    const { data, isLoading, isError } = useGetSubscriptionByIdQuery(id ?? '', { skip: !id });

    // State-based ref so the component re-renders once the DOM element is
    // available, allowing the IntersectionObserver to attach correctly.
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);

    const { activeId, scrollToSection } = useScrollSpy({
        sectionIds: SECTION_IDS,
        root: scrollContainer,
    });

    const sidebarItems = useMemo(
        () =>
            INSTANCE_SECTIONS.map((s) => ({
                id: s.id,
                label: intl.formatMessage(s.label),
            })),
        [intl],
    );

    const handleSidebarClick = useCallback(
        (sectionId: string) => scrollToSection(sectionId),
        [scrollToSection],
    );

    const contextValue = useMemo(() => (data ? { subscription: data } : undefined), [data]);

    const statusTone = data ? STATE_TO_TONE[data.state] : undefined;
    const statusLabel = (() => {
        if (!data) return undefined;
        if (data.state === 'A') return intl.formatMessage(i18n.statusActive);
        if (data.state === 'I') return intl.formatMessage(i18n.statusInactive);
        if (data.state === 'X') return intl.formatMessage(i18n.statusArchived);
        return undefined;
    })();

    const sectionContent = (() => {
        if (isLoading) {
            return (
                <Box className={classes.sectionState} automation-id="instance-detail-loading">
                    <CircularProgress />
                </Box>
            );
        }
        if (isError || !data) {
            return (
                <Box className={classes.sectionState}>
                    <EmptyState
                        title={intl.formatMessage(i18n.loadErrorTitle)}
                        message={intl.formatMessage(i18n.loadErrorMessage)}
                    />
                </Box>
            );
        }
        return (
            <InstanceContext.Provider value={contextValue!}>
                {INSTANCE_SECTIONS.map((section) => (
                    <Box key={section.id} id={section.id} className={classes.sectionBlock}>
                        {!section.hideHeading && (
                            <Typography variant="h5" className={classes.sectionHeading}>
                                {intl.formatMessage(section.label)}
                            </Typography>
                        )}
                        {section.element}
                    </Box>
                ))}
            </InstanceContext.Provider>
        );
    })();

    return (
        <Box className={classes.root} automation-id="instance-detail-page">
            {data && (
                <>
                    {data.state !== 'A' && <InactiveInstanceBanner />}
                    <Box className={classes.header}>
                        <Typography variant="h5" className={classes.headerTitle}>
                            {data.subscriptionName ||
                                intl.formatMessage(routesI18n.untitledInstance)}
                        </Typography>
                        {statusTone && statusLabel && (
                            <Chip
                                size="small"
                                label={statusLabel}
                                color={TONE_TO_CHIP_COLOR[statusTone]}
                                variant={statusTone === 'active' ? 'filled' : 'outlined'}
                                automation-id="instance-detail-status"
                            />
                        )}
                    </Box>
                </>
            )}
            <Box ref={setScrollContainer} className={classes.body}>
                <Box className={classes.sidebarColumn}>
                    <SubSideBar
                        items={sidebarItems}
                        selectedId={activeId}
                        onItemClick={handleSidebarClick}
                        ariaLabel={intl.formatMessage(i18n.subSideBarLabel)}
                    />
                </Box>
                <Box className={classes.section}>{sectionContent}</Box>
            </Box>
        </Box>
    );
};
